import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { Tubing, TubingWearReport } from 'src/app/interfaces/well-integrity';
import { DialogService } from 'src/app/services/dialog.service';
import { HandoverService } from 'src/app/services/handover.service';
import { NotificationService } from 'src/app/services/notification.service';
import { SectionTouched } from '../../shared/section-touched.class';

@Component({
  selector: 'app-handover-detail-well-integrity-tubing-wear',
  templateUrl: './handover-detail-well-integrity-tubing-wear.component.html',
  styleUrls: ['./handover-detail-well-integrity-tubing-wear.component.scss'],
  standalone: false,
})
export class HandoverDetailWellIntegrityTubingWearComponent
  extends SectionTouched
  implements OnInit
{
  @Input() handoverId: number | undefined;
  @Input() wellId: string | undefined;
  @Input() readOnlyContent!: boolean;
  @Output() isLoading = new EventEmitter<boolean>();

  tubings: Tubing[] = [];
  availableTubings: Tubing[] = [];
  tubingWearReports: TubingWearReport[] = [];
  originalTubingWearReports: TubingWearReport[] = [];
  nextTubingWearReport = { enableSave: false } as TubingWearReport;
  nextTubingWearReportIsEnabled = false;
  idOtherTubings = 5;

  displayedColumns = [
    'tubing',
    'maximumPenetration',
    'maximumPenetrationDepth',
    'actions',
  ];

  constructor(
    public handoverService: HandoverService,
    public dialogService: DialogService,
    public notificationService: NotificationService
  ) {
    super();
  }

  ngOnInit(): void {
    this.retrieveTubingWear();
  }

  retrieveTubingWear(): void {
    this.isLoading.emit(true);
    this.handoverService.getTubings().subscribe((result: Tubing[]) => {
      this.tubings = result;
      if (this.handoverId && this.wellId) {
        this.handoverService.getTubingWearReports(this.handoverId).subscribe(
          (reports: TubingWearReport[]) => {
            this.tubingWearReports = reports;
            // Disable save on newly obtained reports
            this.tubingWearReports.forEach((report) => {
              report.enableSave = false;
            });
            this.filterTubings();
            this.setOriginalTubingWearReportValues(reports);
            this.isLoading.emit(false);
          },
          () => this.isLoading.emit(false)
        );
      }
    });
  }

  setOriginalTubingWearReportValues(reports: TubingWearReport[]): void {
    reports.map((val) => {
      if (val.comments == null) val.comments = '';
      this.originalTubingWearReports.push(Object.assign({}, val));
    });
  }

  /**
   * Helps filtering already reported elements away from
   * this.tubings collection.
   */
  filterTubings(): void {
    this.availableTubings = this.tubings;
    this.tubingWearReports.forEach((report) => {
      this.availableTubings = this.availableTubings.filter((element) => {
        return element.tubingId != report.tubing.tubingId;
      });
    });
  }

  /**
   * Enables the row's save button and the other fields if
   * rowIndex is null, which happens when enabling the new report row.
   * @param rowIndex index of the row intended to enable
   */
  enableRow(rowIndex: number | null): void {
    if (rowIndex == null) {
      this.nextTubingWearReportIsEnabled =
        this.nextTubingWearReport.tubing != null;
      if (
        this.nextTubingWearReport.tubing &&
        this.nextTubingWearReport.maximumPenetration &&
        this.nextTubingWearReport.maximumPenetrationDepth &&
        (this.nextTubingWearReport.tubing.tubingId != this.idOtherTubings ||
          this.nextTubingWearReport.comments)
      ) {
        this.nextTubingWearReport.enableSave = true;
        this.emitPendingChanges(true);
      } else {
        this.nextTubingWearReport.enableSave = false;
        this.emitPendingChanges(false);
      }
    } else {
      if (
        this.tubingWearReports[rowIndex].tubing.tubingId !=
          this.originalTubingWearReports[rowIndex].tubing.tubingId ||
        this.tubingWearReports[rowIndex].maximumPenetration !=
          this.originalTubingWearReports[rowIndex].maximumPenetration ||
        this.tubingWearReports[rowIndex].maximumPenetrationDepth !=
          this.originalTubingWearReports[rowIndex].maximumPenetrationDepth ||
        this.tubingWearReports[rowIndex].comments !=
          this.originalTubingWearReports[rowIndex].comments
      ) {
        this.tubingWearReports[rowIndex].enableSave = true;
        this.emitPendingChanges(true);
      } else {
        this.tubingWearReports[rowIndex].enableSave = false;
        this.emitPendingChanges(false);
      }
    }
  }

  openComments(
    report: TubingWearReport,
    index: number | null,
    event: any,
    readOnly: boolean
  ): void {
    const target = new ElementRef(event.currentTarget);
    this.dialogService
      .openCommentDialog(
        'Indique detalle de tubular',
        report?.comments,
        target,
        -200,
        -450,
        readOnly,
        1000
      )
      .afterClosed()
      .subscribe((comments: string) => {
        if (index != null) {
          this.tubingWearReports[index].comments = comments;
          this.enableRow(index);
        } else {
          report.comments = comments;
          this.enableRow(index);
        }
      });
  }

  save(reportToSave: TubingWearReport): void {
    if (this.handoverId) {
      delete reportToSave.enableSave;
      this.isLoading.emit(true);
      this.handoverService
        .saveTubingWearReports(this.handoverId, [reportToSave])
        .subscribe((savedReports: TubingWearReport[]) => {
          this.isLoading.emit(false);
          // If we are saving a new report, then unset the placeholder after being saved.
          if (
            this.nextTubingWearReport.tubing &&
            reportToSave.tubing.tubingId ==
              this.nextTubingWearReport.tubing.tubingId
          ) {
            this.nextTubingWearReport = {
              enableSave: false,
            } as TubingWearReport;
            this.nextTubingWearReportIsEnabled = false;
            this.emitPendingChanges(false);
          }
          // Reload values to put things in order
          this.retrieveTubingWear();
        });
    }
  }

  deleteReport(reportToDelete: TubingWearReport): void {
    this.dialogService
      .openConfirmDialog(
        'Eliminar reporte',
        `¿Está seguro que desea eliminar el reporte para ${reportToDelete.tubing.name}?`,
        'Eliminar',
        'No, gracias'
      )
      .afterClosed()
      .subscribe((accept: boolean) => {
        if (accept && this.handoverId) {
          this.isLoading.emit(true);
          this.handoverService
            .deleteTubingWearReport(this.handoverId, reportToDelete)
            .subscribe((deleted: boolean) => {
              if (deleted) {
                this.notificationService.notify(
                  'Reporte eliminado con éxito',
                  true
                );
                // Reload values to put things in order
                this.retrieveTubingWear();
              }
            });
        }
      });
  }
}
